import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { money, statusClass, formatDate } from "@/lib/utils-app";
import OSEditModal from "./OSEditModal";
import { useStores, storeQS } from "@/lib/stores";
import { useAuth } from "@/lib/auth";
import WhatsAppButton from "@/components/WhatsAppButton";
import { toast } from "sonner";
import { Search, Trash2 } from "lucide-react";

export default function AdminPending() {
  const { user } = useAuth();
  const isSuper = user?.role === "superadmin";
  const { activeStore } = useStores();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ status: "pending" });
      if (q) qs.append("q", q);
      if (activeStore) qs.append("store_id", activeStore);
      const { data } = await api.get(`/admin/service-orders?${qs.toString()}`);
      setRows(data);
    } catch (e) {
      if (e?.response?.status !== 401) toast.error(e.response?.data?.detail || "Falha ao carregar");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [activeStore]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q]);

  const removeOs = async (e, os) => {
    e.stopPropagation();
    if (!window.confirm(`Excluir definitivamente a ${os.os_code}? Fotos, pagamentos e histórico serão perdidos.`)) return;
    try {
      await api.delete(`/admin/service-orders/${os.os_code}`);
      toast.success(`${os.os_code} excluída.`);
      load();
    } catch (e2) { toast.error(e2.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-neutral-500 mb-3">Fila operacional</div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">Serviços Pendentes</h1>
        </div>
        <div className="relative w-full sm:w-80">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="CPF, CNPJ ou OS-XXXXX" className="h-11 pl-9 font-mono" data-testid="pending-search" />
        </div>
      </div>

      <div className="mt-8 bg-white border border-neutral-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200 text-neutral-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">OS</th>
                <th className="px-4 py-3 text-left font-semibold">Cliente</th>
                <th className="px-4 py-3 text-left font-semibold">Documento</th>
                <th className="px-4 py-3 text-left font-semibold">Aparelho</th>
                <th className="px-4 py-3 text-left font-semibold">Status</th>
                <th className="px-4 py-3 text-right font-semibold">Total</th>
                <th className="px-4 py-3 text-left font-semibold">Contato</th>
                <th className="px-4 py-3 text-left font-semibold">Criado</th>
                {isSuper && <th className="px-4 py-3 text-right font-semibold">Ações</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {loading && <tr><td colSpan={isSuper ? 9 : 8} className="p-8 text-center text-neutral-500">Carregando…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={isSuper ? 9 : 8} className="p-8 text-center text-neutral-500">Nenhum serviço pendente.</td></tr>}
              {rows.map((o) => (
                <tr key={o.os_code} onClick={() => setSelected(o.os_code)} className="hover:bg-neutral-50 cursor-pointer" data-testid={`pending-row-${o.os_code}`}>
                  <td className="px-4 py-3 font-mono font-bold">{o.os_code}</td>
                  <td className="px-4 py-3">{o.customer_name} {o.customer_surname}</td>
                  <td className="px-4 py-3 font-mono text-neutral-600">{o.customer_doc}</td>
                  <td className="px-4 py-3 text-neutral-700">{o.device_brand} {o.device_model}</td>
                  <td className="px-4 py-3"><span className={statusClass(o.status)}>● {o.status}</span></td>
                  <td className="px-4 py-3 text-right font-mono">{money(o.total)}</td>
                  <td className="px-4 py-3">
                    <WhatsAppButton
                      phone={o.customer_phone}
                      message={`Olá ${o.customer_name}, aqui é da GTec sobre a OS ${o.os_code} (${o.device_brand} ${o.device_model}).`}
                      testid={`pending-wa-${o.os_code}`}
                      size="xs"
                    />
                  </td>
                  <td className="px-4 py-3 text-neutral-500 text-xs">{formatDate(o.created_at)}</td>
                  {isSuper && (
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="outline" onClick={(e) => removeOs(e, o)} className="text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50" data-testid={`del-os-${o.os_code}`}>
                        <Trash2 size={12}/>
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && <OSEditModal osCode={selected} onClose={() => { setSelected(null); load(); }} />}
    </div>
  );
}
